// CandleVigil.js — "Light a candle."
// The one-second gesture of care: a click lights a candle that burns on the
// page for 24 hours with your name. The row of living flames is the vigil
// happening right now; the total is the love accumulated over time.
//
// Data: memorials/{id}/candles — { name, userId, litAt }
// A candle "burns" for 24 hours after litAt. Requires sign-in to light
// (matching the memory wall); rules for this subcollection are in
// ENGAGEMENT_FIRESTORE_RULES.txt.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection, addDoc, onSnapshot, query, where, orderBy,
  serverTimestamp, Timestamp, getCountFromServer,
} from 'firebase/firestore';
import { db, auth } from '../firebase';

const BURN_HOURS = 24;

function timeLeft(litAt) {
  if (!litAt?.toDate) return '';
  const expires = litAt.toDate().getTime() + BURN_HOURS * 3600 * 1000;
  const hours = Math.max(0, Math.round((expires - Date.now()) / 3600000));
  if (hours >= 2) return `${hours}h of light left`;
  return 'fading soon';
}

function Candle({ candle }) {
  return (
    <div className="flex flex-col items-center w-20">
      {/* Flame */}
      <div className="relative h-8 flex items-end justify-center">
        <div
          className="w-3 h-6 rounded-full"
          style={{
            background: 'radial-gradient(circle at 50% 80%, #ffdf8e 0%, #ffb84d 55%, rgba(255,120,40,0.0) 100%)',
            filter: 'blur(0.4px)',
            animation: 'candleFlicker 1.6s ease-in-out infinite',
            transformOrigin: '50% 100%',
          }}
        />
        <div
          className="absolute bottom-0 w-8 h-8 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,200,110,0.35) 0%, transparent 70%)',
            animation: 'candleGlow 2.4s ease-in-out infinite',
          }}
        />
      </div>
      {/* Wax */}
      <div
        className="w-2.5 h-7 rounded-sm"
        style={{ background: 'linear-gradient(180deg, #fdf6e3, #efe3c3)', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}
      />
      <p className="mt-2 text-[11px] text-gray-700 font-light text-center leading-tight break-words w-full">
        {candle.name || 'Anonymous'}
      </p>
      <p className="text-[9px] text-gray-400 mt-0.5 text-center">{timeLeft(candle.litAt)}</p>
    </div>
  );
}

function CandleVigil({ memorialId, memorial }) {
  const [burning, setBurning] = useState([]);
  const [total, setTotal] = useState(null);
  const [lighting, setLighting] = useState(false);
  const [error, setError] = useState('');

  // Live candles from the last 24 hours
  useEffect(() => {
    if (!memorialId) return undefined;
    const cutoff = Timestamp.fromMillis(Date.now() - BURN_HOURS * 3600 * 1000);
    const q = query(
      collection(db, 'memorials', memorialId, 'candles'),
      where('litAt', '>', cutoff),
      orderBy('litAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => setBurning(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn('candles listener error:', err)
    );
    return unsub;
  }, [memorialId]);

  // Total candles ever lit
  useEffect(() => {
    if (!memorialId) return;
    getCountFromServer(collection(db, 'memorials', memorialId, 'candles'))
      .then((snap) => setTotal(snap.data().count))
      .catch(() => setTotal(null));
  }, [memorialId, burning.length]);

  const user = auth.currentUser;
  const myCandleBurning = useMemo(
    () => !!user && burning.some((c) => c.userId === user.uid),
    [burning, user]
  );

  const lightCandle = useCallback(async () => {
    if (!user || myCandleBurning) return;
    setLighting(true);
    setError('');
    try {
      const name =
        user.displayName ||
        (user.email ? user.email.split('@')[0] : 'A visitor');
      await addDoc(collection(db, 'memorials', memorialId, 'candles'), {
        name,
        userId: user.uid,
        litAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn('Failed to light candle:', e);
      setError('The candle could not be lit. Please try again.');
    } finally {
      setLighting(false);
    }
  }, [user, myCandleBurning, memorialId]);

  return (
    <div id="candle-vigil" className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      <div className="text-center mb-8">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
          Candlelight Vigil
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
          Light a Candle
        </h2>
        <div
          className="mx-auto mt-4"
          style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
        />
        <p className="mt-4 text-gray-500 text-sm font-light">
          A small flame, kept for {memorial?.firstName || 'them'} — each candle burns for a day
        </p>
      </div>

      {/* The vigil */}
      {burning.length > 0 ? (
        <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-8">
          {burning.slice(0, 18).map((c) => (
            <Candle key={c.id} candle={c} />
          ))}
          {burning.length > 18 && (
            <div className="flex items-center text-xs text-gray-400 font-light">
              +{burning.length - 18} more flames
            </div>
          )}
        </div>
      ) : (
        <p className="text-center text-gray-400 text-sm font-light mb-8">
          The vigil awaits its first flame.
        </p>
      )}

      {/* Light one */}
      <div className="text-center">
        {user ? (
          <button
            onClick={lightCandle}
            disabled={lighting || myCandleBurning}
            className="sm-btn-primary"
          >
            {myCandleBurning
              ? '🕯️ Your candle is burning'
              : lighting
                ? 'Lighting…'
                : `🕯️ Light a candle for ${memorial?.firstName || 'them'}`}
          </button>
        ) : (
          <button
            onClick={() => { window.location.href = '/login'; }}
            className="sm-btn-primary"
          >
            🕯️ Log in to light a candle
          </button>
        )}
        {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

        <p className="mt-4 text-[11px] uppercase text-gray-400" style={{ letterSpacing: '0.22em' }}>
          {burning.length} burning now{total !== null && total > 0 ? ` · ${total} lit in remembrance` : ''}
        </p>
      </div>

      <style>{`
        @keyframes candleFlicker {
          0%, 100% { transform: scaleY(1) scaleX(1) rotate(-1deg); opacity: 0.95; }
          25% { transform: scaleY(1.12) scaleX(0.94) rotate(1.5deg); opacity: 1; }
          50% { transform: scaleY(0.94) scaleX(1.05) rotate(-2deg); opacity: 0.9; }
          75% { transform: scaleY(1.08) scaleX(0.96) rotate(1deg); opacity: 1; }
        }
        @keyframes candleGlow {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}

export default CandleVigil;
